import type { FacetType, AggregationOption, MappedAggregation } from '@robustastudio/e-commerce/common';
import { isNumber } from 'lodash-es';

type OmittedMappedAggregation = Omit<MappedAggregation, 'value' | 'options'>;
export interface IFacet {
  label: string;
  type: FacetType;
  code: string;
  options: AggregationOption[] | AggregationOption | number[] | boolean | null;
}

interface IGenericFacet extends IFacet {
  value: AggregationOption[] | null | undefined;
  options: AggregationOption[] | AggregationOption | number[];
  type: FacetType;
}

export interface ISelectFacet extends IFacet {
  value: AggregationOption[] | null | undefined;
  options: AggregationOption[];
  type: FacetType.Select | FacetType.Toggle;
}

interface IRadioSelectFacet extends IFacet {
  value: AggregationOption | null | undefined;
  options: AggregationOption[];
  type: FacetType.Select | FacetType.Toggle;
}

export interface IPriceFacet extends IFacet {
  value: number[] | null | undefined;
  options: number[];
  type: FacetType.Price;
}

export interface IToggleFacet extends IFacet {
  value: AggregationOption | null;
  options: AggregationOption;
  type: FacetType.Toggle;
}

export interface IHiddenFacet extends IFacet {
  type: FacetType.Hidden;
  options: never;
  value: never;
}

export type AllFacets = ISelectFacet | IPriceFacet | IHiddenFacet | IToggleFacet | IRadioSelectFacet;

type MappedPriceProps = OmittedMappedAggregation & IPriceFacet;
type MappedGenericProps = OmittedMappedAggregation & IGenericFacet;

// type MappedToggleProps = OmittedMappedAggregation & IToggleProps[];

type MappedSelectProps = OmittedMappedAggregation & ISelectFacet;

export type MappedProps = MappedPriceProps | MappedSelectProps | MappedGenericProps;

export const isSelectProps = (props: IFacet): props is ISelectFacet => props.type === 'select';
export const isPriceProps = (props: IFacet): props is IPriceFacet => props.type === 'price';
export const isToggleProps = (props: IFacet): props is IToggleFacet => {
  return props.type === 'toggle';
};

export const isPricePropsValue = (props: MappedProps): props is MappedPriceProps => props.type === 'price';
export const isSelectPropsValue = (props: MappedProps): props is MappedSelectProps => props.type === 'select';

export type MappedValue = Array<MappedProps>;

/**
 *
 * @param originalFacets need to the original facets in order to get the correct option values
 * @param facets the facets that need to be converted to structured facets
 * @returns
 */
export const convertToStructuredFacets = (
  originalFacets: MappedAggregation[],
  facets: MappedValue,
): MappedAggregation[] => {
  // map the original facets with the facets options values.
  const originalFacetsOptionsMap = originalFacets.reduce<{
    [key: string]: AggregationOption[];
  }>((acc, facet) => {
    acc[facet.code] = facet.options as AggregationOption[];
    return acc;
  }, {});

  return originalFacets.map(originalFacet => {
    const facet = facets.find(facet => facet.code === originalFacet.code);
    return {
      ...originalFacet,
      value: originalFacetsOptionsMap[facet?.code ?? ''],
    };
  });
};

export const convertStructuredFacet = (facets: MappedAggregation[]): MappedValue => {
  return facets.map<MappedProps>(facet => {
    if (facet.type === 'price' && Array.isArray(facet.value) && isNumber(facet.value[0]) && isNumber(facet.value[1])) {
      return {
        ...facet,
        value: [facet.value?.[0], facet.value?.[1]],
      };
    }

    if (facet.type === 'select') {
      return {
        ...facet,
        value: facet.value && Array.isArray(facet.value) ? facet.value : facet.value ? [facet.value] : [],
        options: facet.options,
      };
    }

    return {
      ...facet,
      value: facet.value as AggregationOption[],
    };
  });
};
